import { Directive, ElementRef, EventEmitter, HostBinding, HostListener, Output } from '@angular/core';
import { Observable, Subject, fromEvent, merge, of } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import { WindowRef } from '../../utils/window-ref';
import { RangeSliderSlideValue } from './range-slider';

// Ohne dies wirft IE Exception.
// eslint-disable-next-line no-var
declare var TouchEvent: any;

/**
 * Eine Direktive, die stellt ein Slider Button dar und wird die Mausstrecke zurückgeliefert.
 * Jede "mousedown" Event generiert ein neues Rx Subject, das durch "changeBegin" EventEmitter geliefert wird.
 */
@Directive({
    selector: `[rangeSliderButton]`
})
export class RangeSliderButtonDirective {

    /**
     * Wird dann aufgerufen, wenn die Maustaste gedruck würde.
     * Liefert eine Observable, die die Mausbewegung- oder Touchbewegung- Event wirft. Wenn die Maustaste freigelasst würde,
     * wird Observable.complete ausgeführt.
     */
    @Output() changeBegin: EventEmitter<Observable<RangeSliderSlideValue>> = new EventEmitter();

    @Output() touch: EventEmitter<boolean> = new EventEmitter();

    @HostBinding('class.dragged') dragActive: boolean = false;

    /**
     * Wird bei gedrückter Maustaste gesetzt und wird dann in obere Komponente für die Mausstrecke-Berechnung benutzt.
     */
    private initialPosition: number;

    constructor(
        private windowRef: WindowRef,
        private elementRef: ElementRef
    ) {
    }

    @HostListener('mousedown', ['$event']) startDrag(e: MouseEvent) {

        // Standardmäßig stell der Browser ein "Sperr-Icon" dar, wenn man ein Element zu verschieben versucht.
        // Um diesen Effekt zu verhindern stehen hier preventDefault und stopPropagation.
        e.preventDefault();
        e.stopPropagation();
        this.markAsStarted(e.clientX);
        const end$: Subject<void> = new Subject<void>();

        const mouseMoveEvent$ = fromEvent<MouseEvent>(this.windowRef.nativeWindow, 'mousemove')
            .pipe(map((e: MouseEvent) => e.clientX));
        const mouseMove$ = this.valueFromEvent(mouseMoveEvent$, end$);

        this.changeBegin.emit(mouseMove$);

        fromEvent(this.windowRef.nativeWindow, 'mouseup')
            .pipe(takeUntil(end$))
            .subscribe(() => this.markAsFinished(end$));

        // focus was denied by e.preventDefault();
        this.elementRef.nativeElement.focus();
    }

    @HostListener('touchstart', ['$event']) startTouchDrag(e: TouchEvent) {
        const touchedItem = e.touches[0];
        this.markAsStarted(touchedItem.clientX);
        const end$: Subject<void> = new Subject<void>();

        const touchMoveEvent$ = fromEvent<TouchEvent>(this.windowRef.nativeWindow, 'touchmove')
            .pipe(
                filter((e: TouchEvent) => e.touches.length === 1),
                map((e: TouchEvent) => e.touches[0].clientX)
            );
        const touchMove$ = this.valueFromEvent(touchMoveEvent$, end$);

        this.changeBegin.emit(touchMove$);

        const touchEnd$ = fromEvent(this.windowRef.nativeWindow, 'touchend');
        const touchCancel$ = fromEvent(this.windowRef.nativeWindow, 'touchcancel');

        merge(touchEnd$, touchCancel$)
            .pipe(takeUntil(end$))
            .subscribe(() => this.markAsFinished(end$));
    }

    @HostListener('keydown.arrowLeft')
    onArrowLeftButton() {
        this.moveTo('left');
    }

    @HostListener('keydown.arrowRight')
    onArrowRightButton() {
        this.moveTo('right');
    }

    /**
     * Markiert die Komponente als Aktiv, bzw:
     *  - Stellt eine CSS-Klasse (dragged) ein;
     *  - Wirft Touch-Event;
     *  - Setzt die "initalPosition"
     */
    private markAsStarted(initialPosition: number): void {
        this.initialPosition = initialPosition;
        this.touch.emit(true);
        this.dragActive = true;
    }

    /**
     * Markiert die Komponent als Inaktiv, bzw:
     * - Löscht CSS-Klasse;
     * - Endet gegebenes endSubject$.
     */
    private markAsFinished(endSubject$: Subject<void>): void {
        this.dragActive = false;
        endSubject$.next();
        endSubject$.complete();
    }

    /**
     * Die Methode vorbereitet eine RangeSliderSlideValue von der Mausposition oder Touchposition.
     */
    private valueFromEvent(position$: Observable<number>, endSubject$: Subject<void>): Observable<RangeSliderSlideValue> {
        return position$
            .pipe(
                takeUntil(endSubject$),
                filter(x => x > 0),
                distinctUntilChanged(),
                map(x => ({
                    initial: this.initialPosition,
                    current: x
                }))
            );
    }

    private moveTo(direction: 'right' | 'left'): void {
        const rect = this.elementRef.nativeElement.getBoundingClientRect();
        const initial = rect.left;
        const diff = rect.width / 3;
        const current = direction === 'left' ? initial - diff : initial + diff;
        this.changeBegin.next(of({
            initial,
            current
        }));
    }
}
